import React from "react";
import { Loader2 } from "lucide-react";
import { AdditionalAssetMeta } from "@amzn/afxtaco-types";
import MarkdownRenderer from "../MarkdownRenderer";
import { useAdditionalAssetContent } from "../../hooks/useAdditionalAssetContent";
import UnsupportedAssetView from "./UnsupportedAssetView";

interface AdditionalAssetTabProps {
  projectId: string;
  asset: AdditionalAssetMeta;
}

/**
 * Renders the content of a single additional asset. Renderable assets (markdown
 * / text) are shown inline via {@link MarkdownRenderer}; everything else falls
 * back to {@link UnsupportedAssetView} with a download button.
 *
 * Downloading a renderable asset is handled by the shared project Download
 * button in the header (alongside the standard assets), so the rendered view
 * itself carries no per-tab download control.
 */
const AdditionalAssetTab: React.FC<AdditionalAssetTabProps> = ({
  projectId,
  asset,
}) => {
  const { content, isLoading, isError, download } = useAdditionalAssetContent({
    projectId,
    path: asset.path,
    isRenderable: asset.isRenderable,
  });

  if (!asset.isRenderable) {
    return (
      <UnsupportedAssetView extension={asset.extension} onDownload={download} />
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="p-6 bg-background">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm text-muted-foreground">Loading...</p>
            </div>
          </div>
        ) : content ? (
          <MarkdownRenderer content={content} projectId={projectId} />
        ) : (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-muted-foreground">
              {isError ? "Failed to load asset" : "No content available"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdditionalAssetTab;
